import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import { Karte, type Kartenpunkt } from '../komponenten/KarteLazy'
import { Wortmarke } from '../komponenten/Wortmarke'

/**
 * Die große Karte — ein eigener Tab, der stehenbleiben darf.
 *
 * <p>Sie kennt zwei Aufträge, die beide über die Adresse kommen:
 *
 * <ul>
 *   <li>{@code ?breite=…&laenge=…&titel=…} — ein einzelner Ort: der Standort eines
 *       Fahrzeugs oder die Stelle, an der ein Unfall gemeldet wurde.</li>
 *   <li>{@code ?flotte=1} — alles, was zuletzt eine Position gemeldet hat.</li>
 * </ul>
 *
 * <p>Der Zustand steckt in der Adresse und nicht in der Anwendung, damit der Tab beim
 * Neuladen dasselbe zeigt und der Link weitergegeben werden kann.
 *
 * <p>Ohne Gerüst und ohne Navigation: Wer diesen Tab öffnet, will die Karte sehen und
 * nicht darin arbeiten. Die Seitenleiste nähme ein Fünftel der Fläche für Wege, die hier
 * niemand geht.
 */

interface Position {
  fahrzeugId: number
  kennzeichen: string
  interneNummer: string | null
  breite: number
  laenge: number
  richtung: number | null
  geschwindigkeit: number | null
  gemessenAm: string
  quelle: string
}

/**
 * Maskiert Text für die Beschriftung am Punkt.
 *
 * <p>Leaflet deutet den Inhalt eines Popups als HTML. Kennzeichen und Wagennummern kommen
 * aus der Datenbank, also aus einem Feld, das jemand tippt — ohne Maskierung stünde dort
 * ein Weg offen, fremdes Markup in die Karte zu bringen.
 */
function sicher(text: string | null | undefined): string {
  if (!text) return ''
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function alter(zeitpunkt: string): string {
  const minuten = Math.round((Date.now() - new Date(zeitpunkt).getTime()) / 60000)
  if (minuten < 1) return 'gerade eben'
  if (minuten < 60) return `vor ${minuten} min`
  const stunden = Math.round(minuten / 60)
  if (stunden < 24) return `vor ${stunden} h`
  return `vor ${Math.round(stunden / 24)} Tagen`
}

export function Kartenansicht() {
  const [parameter] = useSearchParams()
  const flotte = parameter.get('flotte') === '1'
  const breite = Number(parameter.get('breite'))
  const laenge = Number(parameter.get('laenge'))
  const titel = parameter.get('titel') ?? ''

  const [positionen, setPositionen] = useState<Position[]>([])
  const [laedt, setLaedt] = useState(flotte)
  const [fehler, setFehler] = useState<string | null>(null)
  const [standVon, setStandVon] = useState<Date | null>(null)

  const laden = useCallback(() => {
    if (!flotte) return
    setLaedt(true)
    api
      .get<Position[]>('/api/fahrzeuge/positionen')
      .then((p) => {
        setPositionen(p)
        setStandVon(new Date())
        setFehler(null)
      })
      .catch((ex) => setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen'))
      .finally(() => setLaedt(false))
  }, [flotte])

  useEffect(laden, [laden])

  useEffect(() => {
    document.title = flotte ? 'Fahrzeuge auf der Karte' : titel || 'Karte'
  }, [flotte, titel])

  const punkte: Kartenpunkt[] = flotte
    ? positionen.map((p) => ({
        breite: p.breite,
        laenge: p.laenge,
        richtung: p.geschwindigkeit && p.geschwindigkeit > 3 ? p.richtung : null,
        titel: `<strong>${sicher(p.kennzeichen)}</strong>${
          p.interneNummer ? ` · Wagen ${sicher(p.interneNummer)}` : ''
        }<br>${alter(p.gemessenAm)}${
          p.geschwindigkeit != null ? ` · ${p.geschwindigkeit} km/h` : ''
        }`,
      }))
    : Number.isFinite(breite) && Number.isFinite(laenge)
      ? [{ breite, laenge, titel: sicher(titel), betont: true }]
      : []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '10px 18px',
          borderBottom: '1px solid var(--grau-200)',
          background: 'var(--weiss)',
          flexShrink: 0,
        }}
      >
        <Wortmarke />
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ fontSize: '0.95rem' }}>
            {flotte ? 'Fahrzeuge auf der Karte' : titel || 'Standort'}
          </strong>
          <div style={{ color: 'var(--grau-500)', fontSize: '0.8rem' }}>
            {flotte
              ? laedt
                ? 'wird geladen …'
                : `${positionen.length} Fahrzeug${positionen.length === 1 ? '' : 'e'} mit Position` +
                  (standVon ? ` · Stand ${standVon.toLocaleTimeString('de-DE')}` : '')
              : punkte.length > 0
                ? `${breite.toFixed(5)}, ${laenge.toFixed(5)}`
                : 'Kein Standort übergeben'}
          </div>
        </div>
        {flotte && (
          <button type="button" className="knopf-schlicht" onClick={laden} disabled={laedt}>
            Aktualisieren
          </button>
        )}
        {punkte.length === 1 && (
          <a
            className="knopf-schlicht"
            href={`https://www.openstreetmap.org/?mlat=${punkte[0].breite}&mlon=${punkte[0].laenge}#map=17/${punkte[0].breite}/${punkte[0].laenge}`}
            target="_blank"
            rel="noreferrer"
          >
            Bei OpenStreetMap
          </a>
        )}
      </header>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {punkte.length === 0 && !laedt ? (
          <div
            className="leer"
            style={{ display: 'grid', placeItems: 'center', height: '100%', textAlign: 'center' }}
          >
            <div>
              <p style={{ marginBottom: 4 }}>
                {flotte ? 'Kein Fahrzeug hat bisher eine Position gemeldet.' : 'Kein Standort.'}
              </p>
              {flotte && (
                <small style={{ color: 'var(--grau-500)' }}>
                  Positionen entstehen, sobald ein Fahrzeug Daten meldet — heute ist noch
                  keine Telematik angeschlossen.
                </small>
              )}
            </div>
          </div>
        ) : (
          <Karte punkte={punkte} />
        )}
      </div>
    </div>
  )
}
